import { lazy, type ComponentType } from "react"

interface Module<P> {
  default: ComponentType<P>
}

/**
 * `lazy()` for the landing screens that index.html preloads (vite.config.ts,
 * NFR-DSH-01). Still its own chunk, but the import starts when the route table
 * loads, not at first render.
 *
 * Plain `lazy()` suspends on its first render even when the chunk is already
 * in memory: its import promise settles a microtask later. Suspending shows
 * the fallback, and React then holds the revealed screen back for its
 * fallback throttle (about 300 ms), all on the critical path to the largest
 * paint. Once the module has loaded, this hands `lazy()` a thenable that
 * settles synchronously, so React renders the screen at once.
 */
export function preloadedLazy<P = object>(load: () => Promise<Module<P>>) {
  let loaded: Module<P> | undefined
  const pending = load().then((m) => {
    loaded = m
    return m
  })
  // A preload nobody has rendered yet must not fail as an unhandled
  // rejection (offline, a test environment torn down mid-import): the
  // failure belongs to the first render, which still receives `pending`.
  pending.catch(() => undefined)
  return lazy<ComponentType<P>>(() => {
    if (!loaded) return pending
    const ready = loaded
    const settled = {
      then: (onLoaded: (m: Module<P>) => void) => {
        onLoaded(ready)
      },
    }
    return settled as unknown as Promise<Module<P>>
  })
}
